import React, { useState } from 'react';
import { Modal, Form, Button, Alert, InputGroup } from 'react-bootstrap';
import { FaMoneyBillWave, FaSpinner, FaRupeeSign } from 'react-icons/fa';

const WithdrawMoneyModal = ({ show, onHide, onSuccess, availableBalance = 0 }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    amount: '',
    withdrawalMethod: 'bank_transfer',
    accountNumber: '',
    ifscCode: '',
    accountHolderName: '',
    reason: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    const amount = parseFloat(formData.amount);
    
    if (!amount || amount <= 0) {
      setError('Please enter a valid amount');
      return false;
    }
    
    if (amount > availableBalance) {
      setError('Insufficient balance for withdrawal');
      return false;
    }
    
    if (amount < 100) {
      setError('Minimum withdrawal amount is ₹100');
      return false;
    }
    
    if (formData.withdrawalMethod === 'bank_transfer') {
      if (!formData.accountNumber || !formData.ifscCode || !formData.accountHolderName) {
        setError('Please fill all bank details');
        return false;
      }
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    setError('');

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mock success
      if (onSuccess) {
        onSuccess({
          ...formData,
          id: Date.now(),
          status: 'pending',
          requestedAt: new Date().toISOString(),
          amount: parseFloat(formData.amount)
        });
      }
      
      // Reset form
      setFormData({
        amount: '',
        withdrawalMethod: 'bank_transfer',
        accountNumber: '',
        ifscCode: '',
        accountHolderName: '',
        reason: ''
      });
      
      onHide();
    } catch (error) {
      setError('Failed to process withdrawal request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      amount: '',
      withdrawalMethod: 'bank_transfer',
      accountNumber: '',
      ifscCode: '',
      accountHolderName: '',
      reason: ''
    });
    setError('');
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaMoneyBillWave className="me-2" />
          Withdraw Money
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        <div className="mb-3 p-3 bg-light rounded">
          <div className="d-flex justify-content-between align-items-center">
            <span>Available Balance:</span>
            <strong className="text-success">
              <FaRupeeSign /> {availableBalance.toFixed(2)}
            </strong>
          </div>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}
        
        <Form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Withdrawal Amount *</Form.Label>
                <InputGroup>
                  <InputGroup.Text><FaRupeeSign /></InputGroup.Text>
                  <Form.Control
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter amount"
                    min="100"
                    max={availableBalance}
                  />
                </InputGroup>
                <Form.Text className="text-muted">
                  Minimum withdrawal: ₹100
                </Form.Text>
              </Form.Group>
            </div>
            
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Withdrawal Method *</Form.Label>
                <Form.Select
                  name="withdrawalMethod"
                  value={formData.withdrawalMethod}
                  onChange={handleInputChange}
                  required
                >
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="upi">UPI Transfer</option>
                  <option value="wallet">Wallet Transfer</option>
                </Form.Select>
              </Form.Group>
            </div>
          </div>

          {formData.withdrawalMethod === 'bank_transfer' && (
            <>
              <div className="row">
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Account Holder Name *</Form.Label>
                    <Form.Control
                      type="text"
                      name="accountHolderName"
                      value={formData.accountHolderName}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter account holder name"
                    />
                  </Form.Group>
                </div>
                
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Account Number *</Form.Label>
                    <Form.Control
                      type="text"
                      name="accountNumber"
                      value={formData.accountNumber}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter account number"
                    />
                  </Form.Group>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>IFSC Code *</Form.Label>
                    <Form.Control
                      type="text"
                      name="ifscCode"
                      value={formData.ifscCode}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter IFSC code"
                      style={{ textTransform: 'uppercase' }}
                    />
                  </Form.Group>
                </div>
                
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Reason for Withdrawal</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      name="reason"
                      value={formData.reason}
                      onChange={handleInputChange}
                      placeholder="Optional: Reason for withdrawal"
                    />
                  </Form.Group>
                </div>
              </div>
            </>
          )}
        </Form>
      </Modal.Body>
      
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          variant="danger" 
          onClick={handleSubmit}
          disabled={loading}
          className="d-flex align-items-center gap-2"
        >
          {loading && <FaSpinner className="fa-spin" />}
          {loading ? 'Processing...' : 'Request Withdrawal'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default WithdrawMoneyModal;
